// @ts-strict-ignore
import Space from 'antd/lib/space';
import React from 'react';
import {
    AppComponentProps,
    QuickStartCISetupStep,
    QuickStartCISetupStepType,
    QuickStartCISetupViewProps,
} from '../../../typescriptHelpers';
import { InfoTip } from '../../setup/shared';
import { ApiOutlined } from '@ant-design/icons';
import { IntegrationsSetupList } from '../../sharedComponents/IntegrationsSetupList';
import { IntegrationCapability, IntegrationErrorType, IntegrationType } from '../../../types';
import { useOauthIntegrationInstaller } from '../../setup/hooks';
import { useSelector } from 'react-redux';
import { RootState } from '../../../reducers/reducers';
import { selectQuickStartCIStepData, selectVCSIntegration } from '../../../selectors/selectors';
import { QuestionsInfoSection } from '../QuestionsInfoSection';
import { useHistory } from 'react-router-dom';
import { createQuickStartCIStepRoute } from '../../../helpers/routeHelpers';
import { ComingSoonIntegrationCard } from '../../setup/ComingSoonIntegrationCard';

const SetupViewComponent: React.FC<QuickStartCISetupViewProps> = ({ appId, orgId }) => {
    const history = useHistory();

    const isNewApp = !appId;

    const platformStepDataPlatform = useSelector(
        (state: RootState) =>
            selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.PlatformConfirmation)?.platform,
    );

    const vcsIntegrationStepDataNewAppIdParam = useSelector(
        (state: RootState) =>
            selectQuickStartCIStepData(state, appId, QuickStartCISetupStepType.VCSIntegration)?.newAppId,
    );

    React.useEffect(() => {
        if (vcsIntegrationStepDataNewAppIdParam) {
            const appFlowUrl = createQuickStartCIStepRoute(
                orgId,
                vcsIntegrationStepDataNewAppIdParam,
                QuickStartCISetupStepType.VCSIntegration,
            );
            history.replace(appFlowUrl);
        }
    }, [vcsIntegrationStepDataNewAppIdParam]);

    // to intercept oauth installation redirects
    useOauthIntegrationInstaller({
        shouldUseQuickStartCIInstallation: isNewApp,
        platform: platformStepDataPlatform,
    });

    return (
        <div
            style={{
                padding: 10,
                display: 'flex',
                flexDirection: 'column',
                gap: 36,
            }}
        >
            <IntegrationsSetupList
                appId={appId}
                orgId={orgId}
                platform={platformStepDataPlatform}
                shouldUseQuickStartCIInstallation={isNewApp}
                relatedCapabilities={[IntegrationCapability.VersionControl]}
                filterIntegrations={(integrationType) => {
                    return integrationType === IntegrationType.GitHubVCS;
                }}
            />
            <ComingSoonIntegrationCard appId={appId} orgId={orgId} integrationType={IntegrationType.GitLabVCS} />
            <ComingSoonIntegrationCard appId={appId} orgId={orgId} integrationType={IntegrationType.Codemagic} />
            <ComingSoonIntegrationCard appId={appId} orgId={orgId} integrationType={IntegrationType.AzureVCS} />
        </div>
    );
};

const InfoViewComponent: React.FC<AppComponentProps> = () => {
    return (
        <Space direction="vertical">
            <InfoTip>
                After connecting to GitHub and answering a few questions about your setup, the Quickstart wizard will
                automatically generate a GitHub Actions workflow file, fastlane Fastfile, and anything else you might
                need to get rolling.
            </InfoTip>
            <InfoTip>Support for more CI/CD providers is coming soon.</InfoTip>
            <QuestionsInfoSection />
        </Space>
    );
};

export const VCSIntegration: QuickStartCISetupStep = {
    icon: () => ApiOutlined,
    headerTitle: 'Connect to GitHub to allow Runway to gather config details from your repo.',
    SetupViewComponent,
    InfoViewComponent,
    isCompleteSelector: (state, { appId }) => {
        if (!appId) {
            return false;
        }
        const vcsIntegration = selectVCSIntegration(state, appId);
        return (
            !!vcsIntegration &&
            (!vcsIntegration.integrationError ||
                vcsIntegration.integrationError.status === IntegrationErrorType.IncompleteData)
        );
    },
};
